import { useState } from 'react';
import type { CustomerApiClient } from '../api-client.ts';
import { BottomSheet, Button, Card, EmptyState } from '@beautyburst/ui';
import { semanticColor, space, typeStyle } from '@beautyburst/tokens';
import { customerHref } from '../base-path.ts';

type Lists = Awaited<ReturnType<CustomerApiClient['lists']['list']>>;
type List = Lists[number];
type ListDetail = Awaited<ReturnType<CustomerApiClient['lists']['read']>>;

export interface ListsScreenProps {
  lists: Lists;
  selectedList?: ListDetail | null;
  client: Pick<CustomerApiClient, 'lists'>;
  onChanged?: () => void | Promise<void>;
}

/**
 * Shopping-list mutations use the available lists client methods. Item quantity
 * editing is intentionally not faked because the client currently has no updateItem method.
 */
export function ListsScreen({ lists, selectedList = null, client, onChanged }: ListsScreenProps) {
  const [newName, setNewName] = useState('');
  const [renameId, setRenameId] = useState<string | null>(null);
  const [renameName, setRenameName] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<List | null>(null);

  const refresh = () => {
    void onChanged?.();
  };

  return (
    <section aria-labelledby="lists-title">
      <h1 id="lists-title" style={{ ...typeStyle('headingLg'), color: semanticColor.textPrimary }}>
        Shopping lists
      </h1>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (newName.trim() === '') return;
          void client.lists.create(newName).then(() => {
            setNewName('');
            refresh();
          });
        }}
        style={{ display: 'flex', gap: space[3], alignItems: 'end', marginBottom: space[6] }}
      >
        <label style={{ display: 'grid', gap: space[1], flex: 1 }}>
          <span style={{ ...typeStyle('label'), color: semanticColor.textPrimary }}>
            New list name
          </span>
          <input value={newName} onChange={(event) => setNewName(event.currentTarget.value)} />
        </label>
        <Button type="submit">Create list</Button>
      </form>
      <p style={{ ...typeStyle('bodySm'), color: semanticColor.textSecondary }}>
        Need an idea? Try names like Braiding Day, Wig Install, or Monthly Restock.
      </p>

      {lists.length === 0 ? (
        <section aria-labelledby="lists-status-title">
          <h2
            id="lists-status-title"
            style={{ ...typeStyle('headingSm'), color: semanticColor.textPrimary }}
          >
            Your lists
          </h2>
          <EmptyState
            title="No shopping lists yet"
            description="Create a list for the products you want to remember."
          />
        </section>
      ) : (
        <ul
          aria-label="Shopping lists"
          style={{ display: 'grid', gap: space[3], padding: 0, listStyle: 'none' }}
        >
          {lists.map((list) => (
            <li key={list.id}>
              <Card>
                {renameId === list.id ? (
                  <form
                    onSubmit={(event) => {
                      event.preventDefault();
                      if (renameName.trim() === '') return;
                      void client.lists.rename(list.id, renameName).then(() => {
                        setRenameId(null);
                        refresh();
                      });
                    }}
                    style={{ display: 'flex', gap: space[2] }}
                  >
                    <label style={{ flex: 1 }}>
                      <span className="sr-only">Rename {list.name}</span>
                      <input
                        value={renameName}
                        onChange={(event) => setRenameName(event.currentTarget.value)}
                      />
                    </label>
                    <Button type="submit">Save name</Button>
                  </form>
                ) : (
                  <>
                    <h2
                      style={{
                        ...typeStyle('headingSm'),
                        color: semanticColor.textPrimary,
                        marginTop: 0,
                      }}
                    >
                      <a href={customerHref(`/account/lists/${list.id}`)}>{list.name}</a>
                    </h2>
                    {list.note === null ? null : <p>{list.note}</p>}
                    <div style={{ display: 'flex', gap: space[3], flexWrap: 'wrap' }}>
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setRenameId(list.id);
                          setRenameName(list.name);
                        }}
                      >
                        Rename
                      </Button>
                      <Button variant="ghost" onClick={() => setDeleteTarget(list)}>
                        Delete list
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => {
                          void client.lists.addAllAvailableToCart(list.id).then(refresh);
                        }}
                      >
                        Add available items to cart
                      </Button>
                    </div>
                  </>
                )}
              </Card>
            </li>
          ))}
        </ul>
      )}

      {selectedList === null ? null : (
        <section aria-labelledby="list-detail-title" style={{ marginTop: space[8] }}>
          <h2
            id="list-detail-title"
            style={{ ...typeStyle('headingSm'), color: semanticColor.textPrimary }}
          >
            {selectedList.name}
          </h2>
          {selectedList.items.length === 0 ? (
            <EmptyState
              title="This list is empty"
              description="Add products while you browse."
              action={<a href={customerHref('/categories')}>Browse beauty products</a>}
            />
          ) : (
            <ul
              aria-label={`${selectedList.name} items`}
              style={{ display: 'grid', gap: space[3], listStyle: 'none', padding: 0 }}
            >
              {selectedList.items.map((item) => (
                <li key={item.id}>
                  <Card>
                    <p style={{ ...typeStyle('body'), color: semanticColor.textPrimary }}>
                      Product {item.product_id} · Quantity {item.quantity}
                    </p>
                    <p style={{ ...typeStyle('bodySm'), color: semanticColor.textSecondary }}>
                      Quantity editing will be available when the shopping-list API supports item
                      updates.
                    </p>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        void client.lists.removeItem(selectedList.id, item.id).then(refresh);
                      }}
                    >
                      Remove item
                    </Button>
                  </Card>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <BottomSheet
        open={deleteTarget !== null}
        title="Delete shopping list?"
        onClose={() => setDeleteTarget(null)}
        footer={
          <div style={{ display: 'flex', gap: space[3] }}>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>
              Keep list
            </Button>
            <Button
              onClick={() => {
                if (deleteTarget === null) return;
                void client.lists.delete(deleteTarget.id).then(() => {
                  setDeleteTarget(null);
                  refresh();
                });
              }}
            >
              Delete list
            </Button>
          </div>
        }
      >
        <p>Delete {deleteTarget?.name}? This cannot be undone.</p>
      </BottomSheet>
    </section>
  );
}
